<template>
  <DetailLayoutFullWidth>
    <template slot="header">
      <base-header-financial-dashboard
        :title="$t('invoices.received_header')"
        @change="handleChangeEvent"
        @refresh="refreshPage"
        @print="printDashboard"
      />
    </template>
    <template slot="main">
      <loading :opacity="darkMode ? 0 : 0.5" :active="isGeneralLoading" :is-full-page="false" :z-index="10" color="var(--primary-DEFAULT)"></loading>
      <div class="print-area mt-2 md:mt-6 space-y-5">
        <div class="flex flex-wrap gap-4"> 
          <single-panel class="w-full flex-1 min-w-max" :header="$t('financial_dashboard.total_invoices_received')" :content="totalRecibidas">
            <template slot="footer">
                <span :class="isNegativeValue.Subtitulo1" class="font-semibold">{{ totalRecibidasSubtitle }}</span>
                <span> {{ $t('financial_dashboard.vs_the_previous_period', {period: dataPeriodSubtitle }) }}</span>
            </template>
          </single-panel>
          <single-panel :class="isNegativeValue.total" class="w-full flex-1 min-w-max" :header="$t('financial_dashboard.total_amount_expenses')" :content="importeRecibidas">
            <template slot="footer">
              <span>{{ $t('invoices.taxable_base_short') }}: <span :class="isNegativeValue.Subtitulo2P1" class="font-semibold">{{ baseImponibleRecibidas }}</span></span>
              |
              <span>{{ $t('impuestos.header') }}: <span :class="isNegativeValue.Subtitulo2P2" class="font-semibold">{{ totalImpuestosRecibidas }}</span></span>
            </template>
          </single-panel>
          <single-panel :class="isNegativeValue.pending" class="w-full flex-1 min-w-max content-red-500" :header="$t('financial_dashboard.outstanding_payment')" :content="pendientePagoRecibidas">
            <template slot="footer">
              <span v-safe-html="$tc('financial_dashboard.n_outstanding_invoices', pendingSubtitles, { class: `font-semibold ${isNegativeValue.Subtitulo3}` }, 'integer_group')" />
            </template>
          </single-panel>
          <single-panel :class="isNegativeValue.deducible" class="w-full flex-1 min-w-max" :header="$t('financial_dashboard.input_IVA')" :content="totalImpuestosRecibidas">
            <template v-if="false" slot="footer">
              <span class="text-primary-800 dark:text-primary-400 font-semibold">{{ subtitleDeducible }}</span>
              <span> {{ $t('financial_dashboard.deductible') }}</span>
            </template>
          </single-panel>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <BasePanel class="md:col-span-2">
            <template slot="header">
              <div class="flex flex-wrap justify-between gap-4">
                <h3 class="text-base text-secondary-800 dark:text-darkblue-100 font-medium">{{ $t('financial_dashboard.expenses_received_trend') }} ({{ dataPeriod }})</h3>
                <div v-if="false" class="m-auto flex flex-wrap gap-5 justify-center text-secondary-500 dark:text-darkblue-100 text-sm">
                  <span 
                    @click="toggleDatasetChart(refPanelChart,0)" 
                    class="flex items-center gap-2 cursor-pointer" 
                    :style="{ textDecoration: isDatasetHidden(refPanelChart, 0) ? 'line-through' : 'none' }">
                    <base-dot-icon class="text-primary" />
                    <span>{{ $t('invoices.taxable_base') }}</span>
                  </span>
                  <span 
                    @click="toggleDatasetChart(refPanelChart,1)" 
                    class="flex items-center gap-2 cursor-pointer" 
                    :style="{ textDecoration: isDatasetHidden(refPanelChart, 1) ? 'line-through' : 'none' }">
                    <base-dot-icon class="text-primary-900" />
                    <span>{{ $t('impuestos.header') }}</span>
                  </span>
                </div>
              </div>
            </template>
            <template slot="content">
                <Chart id="chart-panel" 
                  :ref="refPanelChart"
                  type="bar"
                  :show-legend="true"
                  :stacked="true"
                  :expand="true"
                  :charData="chartBarDataStatus"
                  :parse-format="formatValueDate"
                  :x-axis-value-format="formatChartDate"
                  y-axis-value-format="compact"
                  tooltip-y-value-format="currency"
                  y-axis-suffix="€"
                />
            </template>
          </BasePanel>
          <BasePanel>
            <template slot="header">
              <h3 class="text-base text-secondary-800 dark:text-darkblue-100 font-medium">{{ $t('financial_dashboard.tax_distribution') }}</h3>
            </template>
            <template slot="content">
              <div v-if="!desgloseImpuestos.length" class="text-secondary-500 dark:text-darkblue-300 italic">
                {{ $t('app.no_data_text') }}
              </div>
              <template v-else>
                <div class="mb-6 md:mb-2">
                  <Chart id="chart-doughnut" 
                    :ref="refDoughnutChart"
                    type="doughnut"
                    :expand="false"
                    :show-legend="false"
                    class="mb-2"
                    :charData="chartDonutDataStatus"
                    tooltip-value-format="currency"
                    tooltip-suffix="€"
                  />
                </div>
                <div class="mt-5">
                  <div v-for="(tax, index) in desgloseImpuestos" :key="index" class="px-2">
                    <div class="flex flex-wrap justify-between">
                      <div 
                        @click="toggleDatasetChart(refDoughnutChart, index)" 
                        class="flex items-center gap-1 text-base cursor-pointer" 
                        :style="{ textDecoration: isDatasetHidden(refDoughnutChart, index) ? 'line-through' : 'none' }">
                        <base-dot-icon class="m-auto mr-1" :style="{ color: dotColor(index) }" />
                        <span>{{ tax.label }}</span>
                      </div>
                      <div class="flex gap-4">
                        <span class="font-bold">
                          {{ $n(tax.value, 'currency') }} € ({{ $n(tax.porcentaje, 'percent_decimal') }})
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </template>
          </BasePanel>
        </div>
        <BasePanel>
          <template slot="header">
            <h3 class="mb-3">{{ $t('financial_dashboard.top_5_') }}</h3>
          </template>
          <template slot="content">
            <div class="space-y-3">
              <div v-for="proveedor in topProveedores" :key="proveedor.nombre" class="bg-primary-50 dark:bg-darkblue-600 rounded-lg p-2">
                <div class="flex flex-wrap gap-2 lg:gap-0 justify-between">
                  <div class="flex flex-col">
                    <span class="text-secondary-600 dark:text-darkblue-100" v-safe-html="proveedor.Nombre"></span>
                    <span class="text-xs text-secondary-500 dark:text-darkblue-200"><span :class="{ 'text-primary-800 dark:text-primary-400': proveedor.TotalFacturas >= 0, 'text-red-800 dark:text-red-400': proveedor.TotalFacturas < 0 }" class="font-semibold">{{ $n(proveedor.TotalFacturas, 'integer_group')  }}</span> {{ $t('invoices.header').toLowerCase() }}</span>
                  </div>
                  <span :class="{ 'text-primary-900 dark:text-primary-400': proveedor.Total >= 0, 'text-red-900 dark:text-red-400': proveedor.Total < 0 }" class="text-lg font-bold">€{{ $n(proveedor.Total, 'currency')  }}</span>
                </div>
              </div>
            </div>
          </template>
        </BasePanel>
      </div>
    </template>
  </DetailLayoutFullWidth>
</template>

<script>
import moment from 'moment'
import { i18n } from '@/i18n'
import _ from 'lodash'
import { COLORES_DASHBOARD } from '@/config/colores-dashboard'
import Loading from 'vue-loading-overlay'
import DetailLayoutFullWidth from '@/components/layout/DetailLayoutFullWidth.vue'
import SinglePanel from '@/components/SinglePanel.vue'
import Chart from '@/components/charts/Chart.vue'
import colorsMixin from '@/mixins/colorsMixin'

import { getAllCompanies } from '@/services/companies/getAllCompanies'
import { getDashboardFinancieroRecibidas } from '@/services/dashboard/getDashboardFinancieroRecibidas'


export default {
  name: 'financial-dashboard',
  mixins: [colorsMixin],
  components: {
    DetailLayoutFullWidth,
    SinglePanel,
    Loading,
    Chart
  },
  props: {

  },
  data () {
    return {
      refPanelChart: 'panelChart',
      refDoughnutChart: 'doughnutChart',
      chartBarDataStatus: null,
      chartDonutDataStatus: null,
      financialRecibidas: {
        FacturasRecibidas: [],
        Proveedores: []
      }
    }
  },
  computed: {
    id () {
      return this.$route.params.id
    },
    clientId () {
      return this.$store.getters['dashboard/getSelectedClient'] || null
    },
    disableSelect () {
      return !this.cifEmpresa || this.isGeneralLoading 
    },
    activePeriod () {
      return this.$store.getters['financialdashboard/activePeriod']
    },
    isNegativeValue () {
      const isNegativeSubtitle1 = this.dataPerPeriodRecibido?.Subtitulos?.Subtitulo1 < 0
      const isNegativeSubtitle2P1 = this.dataPerPeriodRecibido?.BaseImponible < 0
      const isNegativeSubtitle2P2 = this.dataPerPeriodRecibido?.TotalImpuestos < 0
      const isNegativeSubtitle3 = this.dataPerPeriodRecibido?.Subtitulos?.Subtitulo3 < 0
      return {
        total: { 'content-red-700': this.dataPerPeriodRecibido?.Importe < 0 },
        Subtitulo1: { 'text-red-500': isNegativeSubtitle1, 'text-primary-800 dark:text-primary-400': !isNegativeSubtitle1 },
        pending: { 'content-red-700': this.dataPerPeriodRecibido?.PendientePago < 0 }, 
        Subtitulo2P1: { 'text-red-500': isNegativeSubtitle2P1, 'text-primary-800 dark:text-primary-400': !isNegativeSubtitle2P1 },
        Subtitulo2P2: { 'text-red-500': isNegativeSubtitle2P2, 'text-primary-800 dark:text-primary-400': !isNegativeSubtitle2P2 },
        Subtitulo3: isNegativeSubtitle3 ? 'text-red-500' : 'text-primary-800 dark:text-primary-400',
        deducible: { 'content-red-700': this.dataPerPeriodRecibido?.TotalImpuestos < 0 } 
      }
    },
    dataPeriodSubtitle () {
      return this.activePeriods.find(p => p.id === this.activePeriod)?.nameSingular.toLowerCase() ?? this.activePeriod
    },
    cifEmpresa () {
      return this.$store.getters['financialdashboard/cifEmpresa']
    },
    valueDate () {
      return this.$store.getters['financialdashboard/valueDate']
    },
    currentFormat () {
      return this.$store.getters['financialdashboard/currentFormat']
    },
    tiposEstados: function () {
      return this.$store.getters['config/TIPOS_ESTADO']
    },
    tiposCodigoImpuesto: function () {
      return this.$store.getters['config/TIPOS_CODIGO_IMPUESTO']
    },
    topProveedores () {
      return this.financialRecibidas?.Proveedores ?? []
    },
    isGeneralLoading: function () {
      return this.$store.getters.isLoading
    },
    dataPerPeriodRecibido () {
      let picker = this.valueDate 
      if (this.activePeriod ==='semana') {
        picker = moment(picker).format('[Semana] W')
      }
      if (!picker) {
        return this.financialRecibidas.FacturasRecibidas.at(-1)
      }
      return this.financialRecibidas.FacturasRecibidas.find((p) => p.Fecha === picker) 
    },
    desgloseImpuestos () {
      const result = []
      const breakdown = this.dataPerPeriodRecibido?.DesgloseImpuestos
      if (!Array.isArray(breakdown) || breakdown.length === 0 || !this.chartBarDataStatus?.datasets?.length) {
        return result
      }

      for (let i = 0; i < breakdown.length; i++) {
        const tax = breakdown[i]
        const chartTaxColor = this.chartBarDataStatus.datasets.find(ds => ds.clave === tax.Clave)?.backgroundColor || this.labelColor(i)
        result.push({
          clave: tax.Clave,
          label: tax.Label,
          value: tax.Total,
          color: chartTaxColor,
          porcentaje: tax.Porcentaje,
        })
      }
      return result
    },
    darkMode: function () {
      return this.$store.getters['app/darkMode']
    },
    isLower1024: function () {
      return this.$store.getters['app/getIsLower1024']
    },
    isDatasetHidden () {
      return this.$store.getters['financialdashboard/isDatasetHidden']
    },
    totalRecibidas () {
      return i18n.n(this.dataPerPeriodRecibido?.Total ?? 0, 'integer_group')
    },
    totalRecibidasSubtitle () {
      return `${i18n.n(this.dataPerPeriodRecibido?.Subtitulos?.Subtitulo1 ?? 0, 'percent_decimal')}`
    },
    importeRecibidas () {
      return `${i18n.n(this.dataPerPeriodRecibido?.ImporteTotal ?? 0, 'currency')} €`
    },
    baseImponibleRecibidas () {
      return `${i18n.n(this.dataPerPeriodRecibido?.BaseImponible ?? 0, 'currency')} €`
    },
    pendientePagoRecibidas () {
      return `${i18n.n(this.dataPerPeriodRecibido?.PendientePago ?? 0, 'currency')} €`
    },
    pendingSubtitles () {
      return this.dataPerPeriodRecibido?.Subtitulos?.Subtitulo3 ?? 0
    },
    totalImpuestosRecibidas () {
      return `${i18n.n(this.dataPerPeriodRecibido?.TotalImpuestos ?? 0, 'currency')} €`
    },
    subtitleDeducible () {
      return `${i18n.n(this.dataPerPeriodRecibido?.Subtitulos?.Subtitulo4 ?? 0, 'percent_decimal')}`
    },
    tiposEstado: function () {
      return this.$store.getters['config/TIPOS_ESTADO']
    },
    activePeriods: function () {
      return this.$store.getters['config/ACTIVE_PERIODS']
    },
    dataPeriod () {
      return this.activePeriods.find(p => p.id === this.activePeriod)?.timePeriod ?? this.activePeriod
    },
    formatChartDate () {
      return this.currentFormat.i18nFormat || this.currentFormat.momentDate
    },
    formatValueDate () {
      return this.currentFormat.momentValueDate
    },
    enabledDates () {
      return this.financialRecibidas.FacturasRecibidas.map(df => df.Fecha)
    }
  },
  mounted: async function () {
    
  },
  
  watch: {
    
  },
  methods: {
    labelColor (index) {
      return COLORES_DASHBOARD[index] || 'transparent'
    },
    dotColor (index) {
      return this.chartDonutDataStatus?.datasets[0]?.backgroundColor[index] || 'transparent'
    },
    async getListaCifs () {
      try {
        this.$store.commit('setIsLoading', true)
        const data = await getAllCompanies(this.clientId !== -1 ? { idCliente: this.clientId } : {})
        if (data.blnError === true) {
          this.doFeedback(i18n.t('feedback.error.formated_api_response_error', data))
        } else {
          this.cifEmpresaList = data.CIFSEmpresa
        }
      } catch (error) {
        
      } finally {
        this.$store.commit('setIsLoading', false)
      }
    },
    async getDashboardFinancieroRecibidas (period=this.activePeriod) {
      try {
        this.$store.commit('setIsLoading', true)
        this.financialRecibidas.FacturasRecibidas=[]
        this.financialRecibidas.Proveedores=[]
        const unitTime = this.$store.getters['financialdashboard/momentTimeUnit']
        const params = {
          idCifEmpresa: this.cifEmpresa?.IdCIFEmpresa ?? null,
          fechaFiltro: period,
          FechaFin: moment(this.valueDate, this.currentFormat.momentValueDate).startOf('day').endOf(unitTime).format('YYYY-MM-DDT[00:00:00]'),
        }
        const dataDashboard=await getDashboardFinancieroRecibidas(params)
        if (!dataDashboard.blnError) {
          if (dataDashboard.FacturasRecibidas) {
            for (const data of dataDashboard.FacturasRecibidas) {
              this.financialRecibidas.FacturasRecibidas.push(this.getFacturaRecibidaContent(data))
            }
          }
          if (dataDashboard.Proveedores) {
            for (const data of dataDashboard.Proveedores) {
              this.financialRecibidas.Proveedores.push(this.getProveedorContent(data))
            }
          }
          setTimeout(() => {
            this.loadChartData()
          }, 100)
          setTimeout(() => {
            this.loadDoughnutChartData()
          }, 200)
        } else {
          this.doFeedback(i18n.t('feedback.error.formated_api_response_error', dataDashboard))
        }
      } catch (error) {

      } finally {
        this.$store.commit('setIsLoading', false)
      }
    },
    getFacturaRecibidaContent (item) {
      return {
        ImporteTotal: item.ImporteTotal,
        Subtitulos: item.Subtitulos,
        Fecha: item.Fecha,
        Total: item.Total,
        BaseImponible: item.BaseImponible,
        PendientePago: item.PendientePago,
        TotalImpuestos: item.TotalImpuestos,
        DesgloseImpuestos: item.DesgloseImpuestos.map(this.getDesgloseImpuestosContent).sort(this.sortDesgloseImpuestos)
      }
    },
    sortDesgloseImpuestos (a, b) {
      return parseInt(a.Clase) - parseInt(b.Clase) || parseInt(b.Tipo) - parseInt(a.Tipo)
    },
    getDesgloseImpuestosKey (item) {
      return `${item.Clase}-${item.Tipo}`
    },
    getDesgloseImpuestosContent (item) {
      const taxType = this.tiposCodigoImpuesto.find(t => t.id === item.Clase)?.short ?? item.Clase
      return {
        Clave: this.getDesgloseImpuestosKey(item),
        Label: `${taxType} ${i18n.n(item.Tipo ?? 0, 'percent')}`,
        Clase: item.Clase,
        Tipo: item.Tipo,
        Total: item.Total,
        Porcentaje: item.Porcentaje
      }
    },
    getProveedorContent (item) {
      return {
        Nombre: this.addBreakPoints(item.Nombre),
        TotalFacturas: item.TotalFacturas,
        Total: item.Total
      }
    },
    loadChartData () {
      this.chartBarDataStatus = {
        labels: [],
        datasets: [
          {
            clave: 'baseImponible',
            label: i18n.t('invoices.taxable_base'),
            data: [],
            backgroundColor: this.getColor('--primary-DEFAULT'),
          },
        ]
      }
      const uniqueTaxes = _.uniqBy(this.financialRecibidas.FacturasRecibidas.flatMap(invoice => invoice.DesgloseImpuestos), 'Clave').sort(this.sortDesgloseImpuestos)
      const datasetTaxes = []
      for (let i = 0; i < uniqueTaxes.length; i++) {
        const tax = uniqueTaxes[i]
        datasetTaxes.push({
          clave: tax.Clave,
          label: tax.Label,
          data: [],
          backgroundColor: this.labelColor(i),
          tipo: tax.Tipo,
        })
      }
      if (datasetTaxes) {
        this.chartBarDataStatus.datasets.push(...datasetTaxes)
      }

      this.financialRecibidas.FacturasRecibidas.forEach(element => {
        this.chartBarDataStatus.labels.push(element.Fecha)
        this.chartBarDataStatus.datasets[0].data.push(element.BaseImponible)

        if (this.chartBarDataStatus.datasets.length > 1) {
          for (let index = 1; index < this.chartBarDataStatus.datasets.length; index++) {
            const dataSet = this.chartBarDataStatus.datasets[index]
            dataSet.data.push(element.DesgloseImpuestos.find(tax => tax.Clave === dataSet.clave)?.Total ?? 0)
          }
        }
      })
    },
    loadDoughnutChartData () {
      this.chartDonutDataStatus = {
        labels: [],
        datasets: [
          {
            label: [],
            data: [],
            borderWidth: 0,
            backgroundColor: [],
          }
        ]
      }
      this.desgloseImpuestos.forEach(({ label, value, color }) => {     
        this.chartDonutDataStatus.labels.push(label)
        this.chartDonutDataStatus.datasets[0].backgroundColor.push(color)
        this.chartDonutDataStatus.datasets[0].data.push(value)
      })
    },
    handleChangeEvent ({ field, value, refresh }) {
      if (refresh) {
        if (field !== 'valueDate' || !this.enabledDates.includes(value)) {
          this.refreshPage()
        } else {
          setTimeout(() => {
            // this.loadChartData()
            this.loadDoughnutChartData()
          }, 100)
        }
      }
    },
    async refreshPage () {
      this.$store.commit('financialdashboard/resetHiddenDatasets')
      await this.getDashboardFinancieroRecibidas()
    },
    toggleDatasetChart (refChart, datasetIndex) {
      this.$store.commit('financialdashboard/toggleHiddenDataset', { refChart, datasetIndex })
      this.$refs[refChart].toggleDatasetVisibility(datasetIndex)
    },
    printDashboard () {
      const printArea = this.$el.querySelector('.print-area')
      if (!printArea) return

      const oldIframe = document.getElementById('printeriframe')
      if (oldIframe) oldIframe.remove()

      const clone = printArea.cloneNode(true)
      const originalCanvas = printArea.querySelectorAll('canvas')

      clone.querySelectorAll('canvas').forEach((cloneCanvas, index) => {
        if (originalCanvas[index]) {
          const chartRef = this.$refs[this.refPanelChart]
          const sourceCanvas = chartRef?.trimCanvas() || originalCanvas[index]
          const img = document.createElement('img')
          img.src = sourceCanvas.toDataURL('image/png')
          img.style.width = this.isLower1024 ? '65%' : '100%'
          img.style.justifySelf = 'center'
          img.style.alignSelf = 'center'
          cloneCanvas.parentNode.replaceChild(img, cloneCanvas)
        }
      })

      const iframe = document.createElement('iframe')
      iframe.setAttribute('id', 'printeriframe')
      iframe.setAttribute('width', '768px')
      iframe.style.display = 'none'
      document.body.appendChild(iframe)

      const iframeDoc = iframe.contentDocument || iframe.contentWindow.document

      const allStyles = [...document.querySelectorAll('link[rel="stylesheet"], style')]
        .map(el => el.outerHTML).join('\n')

      iframeDoc.open()
      iframeDoc.write(`<!DOCTYPE html><html><head><meta charset="utf-8">${allStyles}</head><body>${clone.outerHTML}</body></html>`)
      iframeDoc.close()

      const printStyle = iframeDoc.createElement('style')
      printStyle.textContent = `
        @page { 
          size: A4; 
          margin: 10mm;
        }
        @media print {
          .print-area { 
            shadow: none !important; 
          }
          .kpi-card {
              break-inside: avoid;
              page-break-inside: avoid;
          }
        }
        *, *::before, *::after { 
          box-sizing: border-box; 
        }
        html, body {
          width: 100%;
          height: auto;
          margin: 0;
          padding: 0;
          overflow: visible;
          background: white;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        .print-area {
          width: 100%;
          padding: 0;
          margin: 0;
        }
        `
      iframeDoc.head.appendChild(printStyle)

      setTimeout(() => {
        try {
          iframe.contentWindow.focus()
          iframe.contentWindow.print()
        } catch (e) {
          console.error('Error al imprimir:', e)
        }
        setTimeout(() => iframe.remove(), 1000)
      }, 300)
    },
    addBreakPoints (text) {
      const el = document.createElement('div')
      el.textContent = String(text)
      return el.innerHTML.replace(/([_\-.])/g, '$1<wbr>')
    }
  }
}
</script>
<style scoped>
@media print {
  body * {
    visibility: hidden;
  }
  .print-area,.print-area * {
    visibility: visible;
  }
  .print-area {
    left: 0;
    top: 0;
    width: 100%;
  }
}
</style>
